import UseAxios from '../../Api/http/http';
import axios from 'axios';
import { loginAudit, verifyAccessToken } from '../../Api/request/login';
import { generateBearerToken, getUserRoles } from '../../Api/request/userRoles';
import PropTypes from 'prop-types';
import AxiosWrapper from '../../Api/http/AxiosWrapper';
import { AESUtils } from '../../../src/Utils/app_utils';
import { fetchUserInfo } from '../../Api/request/integration';
import { getSiteConfiguration } from '../../Api/request/siteConfiguration';

// Static key and IV
const secretKey = 'a1b2c3d4e5f60718293a4b5c6d7e8f90123456789abcdef0123456789abcdef0'; // 32-byte hex key
const iv = '1234567890abcdef1234567890abcdef'; // 16-byte hex IV
const aesUtils = new AESUtils(secretKey, iv);

const userData = {};
const Wrapper = new AxiosWrapper();

export const configureUserData = async (googleData) => {
  // Map either the OAuth token or the One Tap JWT
  const token = googleData.access_token || googleData.credential;
  userData.access_token = token;
  userData.user_agent = window.navigator.userAgent;
  userData.jwt_token = token;
  userData.login_status = 'success';
  userData.locale = 'en';
  userData.ip_address = await getSystemIP();
  userData.failure_reason = 'NA';

  return UseAxios(loginAudit(userData))
    .then((response) => {
      userData.id = response.id;
      Wrapper.authenticate(userData.access_token);
    })
    .catch((error) => {
      console.error(error);
    });
};

const getTokenFromLocalStorage = () => localStorage.getItem('userRolesToken');
const getPermissionsFromLocalStorage = () => localStorage.getItem('userPermissions');
const setTokenInLocalStorage = (token) => localStorage.setItem('userRolesToken', token);

const removeDataInLocalStorage = () => {
  localStorage.removeItem('activeMenuItem');
  localStorage.removeItem('activeSubmenuItem');
  localStorage.removeItem('activeSubmenuOption');
  localStorage.removeItem('userRolesToken');
  localStorage.removeItem('userPermissions');
};

const setPermissionsInLocalStorage = (permissions) => {
  const encryptedPermissions = aesUtils.encrypt(JSON.stringify(permissions));
  localStorage.setItem('userPermissions', encryptedPermissions);
};

const fetchAndStoreUserRoles = async (token, email) => {
  try {
    const roles = await getLoggedInUserRoles(token, email);
    setPermissionsInLocalStorage(roles);
    return roles;
  } catch (error) {
    removeDataInLocalStorage();
    console.error(error);
    throw error;
  }
};

export const getBearerToken = async (email) => {
  try {
    const token = getTokenFromLocalStorage();
    if (token) {
      const encryptedPermissions = getPermissionsFromLocalStorage();
      if (encryptedPermissions) {
        return JSON.parse(aesUtils.decrypt(encryptedPermissions));
      } else {
        return await fetchAndStoreUserRoles(token, email);
      }
    } else {
      const response = await UseAxios(generateBearerToken(email));
      const userRolesToken = response.data.data.token;
      setTokenInLocalStorage(userRolesToken);
      return await fetchAndStoreUserRoles(userRolesToken, email);
    }
  } catch (error) {
    console.error(error);
    removeDataInLocalStorage();
    throw error;
  }
};

const getLoggedInUserRoles = async (token, mailId) => {
  return UseAxios(getUserRoles(token, mailId))
    .then((response) => {
      const userRoles = response.data.data[0];
      return userRoles;
    })
    .catch((error) => {
      console.error(error);
    });
};

const getSystemIP = async () => {
  try {
    // if (process.env.NODE_ENV === 'production') {
    //   return 'unknown';
    // }
    const res = await axios.get('https://ipinfo.io/json', { timeout: 5000 });
    return res?.data?.ip || 'unknown';
  } catch (error) {
    console.warn('Failed to fetch IP address:', error);
    return 'unknown';
  }
};

configureUserData.propTypes = {
  googleData: PropTypes.object.isRequired,
};

export const verifyToken = async (token) => {
  return UseAxios(verifyAccessToken(token))
    .then(async (response) => {
      const userEmailId = await response?.data?.email;

      if (response?.data?.user_name && response?.data?.email) {
        const sessionTimeOut = response?.data?.access_token_valid_till;
        const data = {
          filterValues: {emailId: userEmailId},
          fieldsToShow: ['employeeId'],
        };

        // Don't let a down Integration MS block login; degrade gracefully instead.
        const employeeId = await UseAxios(fetchUserInfo(data))
          .then((employeeIdResponse) => employeeIdResponse?.data?.users?.data?.[0]?.employeeId)
          .catch((error) => {
            console.error('Error fetching employeeId, continuing login without it:', error);
            return null;
          });
        const siteConfiguration = await UseAxios(getSiteConfiguration())
          .then((res) => res.data)
          .catch((error) => {
            console.error('Error fetching site configuration:', error);
            return null;
          });

        setSessionTimeout(sessionTimeOut);
        response.data.employeeId = employeeId;
        response.data.siteConfiguration = siteConfiguration;

        return response.data;
      } else {
        throw new Error('Token expired');
      }
    })
    .catch((error) => {
      console.error(error);
      removeDataInLocalStorage();
      return null;
    });
};

let sessionTimeoutId = '';

const handleSessionExpiration = (message) => {
  const authToken = Wrapper.getAuthToken();
  if (!authToken) {
    if (sessionTimeoutId) {
      clearTimeout(sessionTimeoutId);
    }
    return;
  }
  if (!document.hidden) {
    alert(message);
  }
  window.location.href = '/';
  removeDataInLocalStorage();
};

const setSessionTimeout = (sessionTimeOut) => {
  const sessionExpiryDate = new Date(sessionTimeOut);
  const currentTime = new Date();
  const remainingTime = sessionExpiryDate - currentTime;

  if (sessionTimeoutId) {
    clearTimeout(sessionTimeoutId);
  }

  const message = 'Session expired, login again';
  const maxTimeOut = 2_147_483_647;

  if (remainingTime > maxTimeOut) {
    sessionTimeoutId = setTimeout(() => {
      setSessionTimeout(sessionExpiryDate.toISOString());
    }, maxTimeOut);
  } else if (remainingTime > 0) {
    sessionTimeoutId = setTimeout(() => {
      handleSessionExpiration(message);
    }, remainingTime);
  } else {
    handleSessionExpiration(message);
  }
};

verifyToken.propTypes = {
  googleData: PropTypes.string.isRequired,
};

getBearerToken.prototype = {
  data: PropTypes.object.isRequired,
};
